/// <reference types="@mobsuccess-devops/axios-instance" />
import axios, { type AxiosInstance, type AxiosResponse } from "axios";

const TRANSIENT_NETWORK_CODES = new Set([
  "ECONNRESET",
  "ECONNABORTED",
  "ETIMEDOUT",
  "EAI_AGAIN",
  "ENETUNREACH",
  // Browsers never surface the node-style codes above: the XHR adapter reports
  // every network-layer failure (drop, DNS, CORS preflight abort) as ERR_NETWORK
  // with status 0, so without this entry no browser blip is ever retried.
  "ERR_NETWORK",
]);

// A failure with no response may still have reached the server, so replaying it
// is only safe for methods that carry no side effect.
const IDEMPOTENT_METHODS = new Set(["get", "head", "options"]);

// Gateway-level failures where the request most likely never reached the
// application handler, so an immediate replay can land on a healthy backend.
// A plain 500 is the handler itself failing — usually deterministic — and
// retrying it here only multiplies with the query-level retry (a dead
// endpoint was observed costing 3 × 3 requests). Queries still retry 500s
// with a slower backoff via defaultOptions.queries.retry.
// 504 is owned by gateway-timeout (single immediate retry, tuned for slow
// cold starts). Skipping it here avoids stacking a fast 100/250ms budget
// on top, which would burn the budget before the slow retry could land.
const TRANSIENT_HTTP_STATUSES = new Set([502, 503]);

const RETRY_DELAYS_MS = [100, 250];

function describeError(err: unknown): string {
  if (!axios.isAxiosError(err)) {
    return "non-axios";
  }
  if (err.response) {
    return `http_status=${err.response.status}`;
  }
  return err.code ?? "no-response";
}

function isTransient(err: unknown): boolean {
  if (!axios.isAxiosError(err)) {
    return false;
  }
  if (err.response) {
    return TRANSIENT_HTTP_STATUSES.has(err.response.status);
  }
  // Axios defaults an absent method to GET, so treat it as idempotent too.
  if (!IDEMPOTENT_METHODS.has(err.config?.method?.toLowerCase() ?? "get")) {
    return false;
  }
  return !!err.code && TRANSIENT_NETWORK_CODES.has(err.code);
}

async function error(
  this: Pick<AxiosInstance, "request">,
  err: unknown,
): Promise<AxiosResponse> {
  if (!axios.isAxiosError(err)) {
    throw err;
  }
  const config = err.config;
  if (!config) {
    throw err;
  }

  const attempt = config._transientRetryAttempt ?? 0;

  if (
    !isTransient(err) ||
    attempt >= RETRY_DELAYS_MS.length ||
    config.doNotRetry
  ) {
    throw err;
  }

  // Intentional console.warn: CloudWatch/devtools visibility for transient retries.
  // oxlint-disable-next-line no-console
  console.warn(
    `MSAxiosInstance: transient failure on ${config.method?.toUpperCase() ?? "?"} ${config.url} ` +
      `(${describeError(err)}) retrying attempt=${attempt + 1}/${RETRY_DELAYS_MS.length}`,
  );

  await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));

  return (this ?? axios).request({
    ...(config.originalConfig ?? config),
    originalConfig: config.originalConfig,
    _transientRetryAttempt: attempt + 1,
  });
}

export const transientRetry = { error };
